import { useCallback, useEffect, useMemo, useState } from 'react';

// import { mobileApi } from '../../helpers/api/clients';
import { isFeatureEnabled } from '../../helpers/featureFlags';
import { STORAGE_KEYS, useStorage } from '../../helpers/useStorage';
import { useSelector } from '../../interfaces/State';
import {
  selectCourse,
  selectStreakDays,
  selectStreakFreezesAwardedInSession,
} from '../../redux/selectors';

function useFirstMobileSignIn({
  skip = false,
}): {
  firstMobileSignInAt?: string | null;
  isLoading: boolean;
} {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [firstMobileSignInAt, setFirstMobileSignInAt] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (skip) {
      setLoading(false);
      setFirstMobileSignInAt(null);
      return;
    }

    setLoading(true);
    setFirstMobileSignInAt(null);

    // mobileApi
    //   .getFirstMobileSignInAt()
    //   .then((res) =>
    //     setFirstMobileSignInAt(res?.body?.data?.user?.firstMobileSignInAt),
    //   )
    //   .catch(() => setFirstMobileSignInAt(null))
    //   .finally(() => setLoading(false));
    setFirstMobileSignInAt(null);
  }, [skip]);

  return {
    firstMobileSignInAt,
    isLoading,
  };
}

type ActiveScreen = 'DAILY_STREAK' | 'FREEZES_AWARDED' | 'MOBILE_APP';

//                           d    h    m    s   ms
const APP_SCREEN_INTERVAL = 90 * 24 * 60 * 60 * 1000; // 90 days

function useDailyStreakNavigation({
  onExit,
  show,
}: {
  onExit: () => void;
  show?: boolean;
}): {
  onNextScreen: () => void;
  screen?: ActiveScreen;
} {
  const streakDays = useSelector(selectStreakDays);
  const freezesAwardedInSession = useSelector(
    selectStreakFreezesAwardedInSession,
  );
  const campusCourseSupported =
    useSelector(selectCourse).get('mobile_enabled') ?? false;

  const [screen, setScreen] = useState<ActiveScreen>('DAILY_STREAK');

  const onFinish = useCallback(() => {
    onExit();
    setScreen('DAILY_STREAK');
  }, [onExit]);

  // Mobile App Screen
  const { firstMobileSignInAt } = useFirstMobileSignIn({ skip: !show });
  const {
    setValue: setLastSeenAppScreen,
    value: lastSeenAppScreen,
  } = useStorage(STORAGE_KEYS.MOBILE_APP_SCREEN_SEEN, 0);

  const shouldShowMobileScreen = useMemo(() => {
    const isSecondDay = streakDays === 2;
    const isCourseSupportedInMobile = campusCourseSupported;
    const hasUsedMobile = !!firstMobileSignInAt;

    if (!isSecondDay || !isCourseSupportedInMobile || hasUsedMobile) {
      return false;
    }

    const shouldShow = Date.now() - lastSeenAppScreen > APP_SCREEN_INTERVAL;

    return shouldShow;
  }, [
    firstMobileSignInAt,
    campusCourseSupported,
    streakDays,
    lastSeenAppScreen,
  ]);

  const hasMobileScreenEnabled = useMemo(() => {
    if (!shouldShowMobileScreen) {
      return false;
    }

    return isFeatureEnabled('wl--q4-2024-campus-engagement-test1');
  }, [shouldShowMobileScreen]);

  useEffect(() => {
    if (
      !hasMobileScreenEnabled ||
      screen !== 'MOBILE_APP' ||
      !shouldShowMobileScreen
    ) {
      return;
    }

    // TODO: maybe send a heap event?
    setLastSeenAppScreen(Date.now());
  }, [
    shouldShowMobileScreen,
    screen,
    hasMobileScreenEnabled,
    setLastSeenAppScreen,
  ]);

  // Freezes
  const onFreezesAwardedExit = useCallback(() => {
    if (hasMobileScreenEnabled && shouldShowMobileScreen) {
      setScreen('MOBILE_APP');
      return;
    }

    onFinish();
  }, [onFinish, hasMobileScreenEnabled, shouldShowMobileScreen]);

  // Initial screen
  const onDailyStreakExit = useCallback(() => {
    if (freezesAwardedInSession) {
      setScreen('FREEZES_AWARDED');
      return;
    }

    if (hasMobileScreenEnabled && shouldShowMobileScreen) {
      setScreen('MOBILE_APP');
      return;
    }

    onFinish();
  }, [
    freezesAwardedInSession,
    hasMobileScreenEnabled,
    shouldShowMobileScreen,
    onFinish,
  ]);

  // Screen navigation
  return {
    screen,
    onNextScreen: useCallback(() => {
      if (screen === 'DAILY_STREAK') {
        return onDailyStreakExit();
      }

      if (screen === 'FREEZES_AWARDED') {
        return onFreezesAwardedExit();
      }

      if (screen === 'MOBILE_APP') {
        return onFinish();
      }

      return onFinish();
    }, [screen, onFinish, onDailyStreakExit, onFreezesAwardedExit]),
  };
}

export default useDailyStreakNavigation;
